import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { 
  addMovement, 
  getMovementsPaginated,
  getMovementsTotals,
  rebuildAggregateStats,
  deleteMovement,
  groupMovementsBySession 
} from '../services/babyMovementService';
import {
  formatDisplayDateTime,
  formatDateRange,
  getISTDateString,
  getISTTimeString
} from '../utils/dateHelpers';
import { ToastContainer, useToast } from './Toast';
import DayWiseStatsChart from './DayWiseStatsChart';

const BabyMovementTracker = () => {
  const { currentUser, isSpecialUser } = useAuth();
  const navigate = useNavigate();
  const { toasts, showToast, removeToast } = useToast();

  // State management
  const [movements, setMovements] = useState([]);
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingRoll, setAddingRoll] = useState(false);
  const [addingKick, setAddingKick] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [rebuildingStats, setRebuildingStats] = useState(false);

  // Filter and view state
  const [viewMode, setViewMode] = useState('individual'); // 'individual' or 'session'
  const [sessionMinutes, setSessionMinutes] = useState(15); // Default 15 minutes
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showChart, setShowChart] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [pageCache, setPageCache] = useState({}); // Cache pages to allow back navigation
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to force refresh
  
  // Totals state (from server)
  const [serverTotals, setServerTotals] = useState({
    totalRolls: 0,
    totalKicks: 0,
    totalMovements: 0,
    count: 0
  });

  // Check access on mount
  useEffect(() => {
    if (!isSpecialUser) {
      showToast('You do not have permission to access this page', 'error');
      navigate('/');
    }
  }, [isSpecialUser, navigate]);

  // Fetch movements on mount and when pagination changes
  useEffect(() => {
    if (currentUser && isSpecialUser) {
      fetchMovements();
    }
  }, [currentUser, isSpecialUser, page, pageSize, refreshTrigger]);

  // Update filtered movements when movements change
  useEffect(() => {
    setFilteredMovements(movements);
  }, [movements]);

  /**
   * Fetch movements from Firestore with pagination
   */
  const fetchMovements = async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      // Check if we have this page cached (skip if force refresh)
      if (!forceRefresh && pageCache[page]) {
        setMovements(pageCache[page].movements);
        setLoading(false);
        return;
      }

      // Determine the last document for pagination
      let lastDoc = null;
      if (page > 1 && pageCache[page - 1]) {
        lastDoc = pageCache[page - 1].lastVisible;
      }

      // Fetch paginated data
      const result = await getMovementsPaginated(
        currentUser.email, 
        pageSize, 
        lastDoc,
        dateRange.start,
        dateRange.end
      );
      
      // Cache the page
      setPageCache(prev => ({
        ...prev,
        [page]: {
          movements: result.movements,
          lastVisible: result.lastVisible
        }
      }));

      setMovements(result.movements);

      // Get total count and aggregated totals only on first page and first load
      if (page === 1 && totalCount === 0) {
        const totals = await getMovementsTotals(
          currentUser.email,
          dateRange.start,
          dateRange.end
        );
        
        // If totals show 0 but we have movements, rebuild stats
        if (!dateRange.start && !dateRange.end && totals.count === 0 && result.movements.length > 0) {
          await rebuildAggregateStats(currentUser.email);
          // Fetch totals again after rebuild
          const rebuiltTotals = await getMovementsTotals(currentUser.email);
          setTotalCount(rebuiltTotals.count);
          setServerTotals(rebuiltTotals);
        } else {
          setTotalCount(totals.count);
          setServerTotals(totals);
        }
      }
    } catch (error) {
      console.error('Error fetching movements:', error);
      showToast('Failed to load movements', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Add a new roll movement
   */
  const handleAddRoll = async () => {
    try {
      setAddingRoll(true);
      const now = new Date();
      
      const movementData = {
        userId: currentUser.email,
        type: 'roll',
        rolls: 1,
        kicks: 0,
        dateIST: getISTDateString(now),
        timeIST: getISTTimeString(now),
        date: now
      };
      
      await addMovement(movementData);
      
      // Update stats locally without refetching everything
      setServerTotals(prev => ({
        totalRolls: prev.totalRolls + 1,
        totalKicks: prev.totalKicks,
        totalMovements: prev.totalMovements + 1,
        count: prev.count + 1
      }));
      setTotalCount(prev => prev + 1);
      
      // Only clear cache and refresh if on page 1 (where new item appears)
      if (page === 1) {
        setPageCache({});
        setRefreshTrigger(prev => prev + 1);
      }
      
      showToast('Roll added successfully! 🎯', 'success');
    } catch (error) {
      console.error('Error adding roll:', error);
      showToast('Failed to add roll', 'error');
    } finally {
      setAddingRoll(false);
    }
  };

  /**
   * Add a new kick movement
   */
  const handleAddKick = async () => {
    try {
      setAddingKick(true);
      const now = new Date();
      
      const movementData = {
        userId: currentUser.email,
        type: 'kick',
        rolls: 0,
        kicks: 1,
        dateIST: getISTDateString(now),
        timeIST: getISTTimeString(now),
        date: now
      };
      
      await addMovement(movementData);
      
      // Update stats locally without refetching everything
      setServerTotals(prev => ({
        totalRolls: prev.totalRolls,
        totalKicks: prev.totalKicks + 1,
        totalMovements: prev.totalMovements + 1,
        count: prev.count + 1
      }));
      setTotalCount(prev => prev + 1);
      
      // Only clear cache and refresh if on page 1 (where new item appears)
      if (page === 1) {
        setPageCache({});
        setRefreshTrigger(prev => prev + 1);
      }
      
      showToast('Kick added successfully! 👶', 'success');
    } catch (error) {
      console.error('Error adding kick:', error);
      showToast('Failed to add kick', 'error');
    } finally {
      setAddingKick(false);
    }
  };

  /**
   * Delete a movement
   */
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }

    try {
      // Find the movement being deleted to update stats correctly
      const movementToDelete = filteredMovements.find(m => m.id === id);
      
      await deleteMovement(id);
      
      if (movementToDelete) {
        // Update stats locally
        setServerTotals(prev => ({
          totalRolls: prev.totalRolls - (movementToDelete.rolls || 0),
          totalKicks: prev.totalKicks - (movementToDelete.kicks || 0),
          totalMovements: prev.totalMovements - 1,
          count: prev.count - 1
        }));
        setTotalCount(prev => Math.max(0, prev - 1));
      }
      
      // Clear cache and trigger refresh for current page
      setPageCache({});
      setRefreshTrigger(prev => prev + 1);
      
      showToast('Entry deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting movement:', error);
      showToast('Failed to delete entry', 'error');
    }
  };

  /**
   * Apply date range filters
   */
  const applyFilters = async () => {
    setPage(1); // Reset to first page
    setPageCache({}); // Clear cache
    setTotalCount(0); // Reset count to force refetch
    setServerTotals({ totalRolls: 0, totalKicks: 0, totalMovements: 0, count: 0 }); // Reset totals
    showToast('Filters applied', 'success');
    
    // This will trigger fetchMovements via useEffect
  };

  /**
   * Reset filters
   */
  const resetFilters = () => {
    setDateRange({ start: '', end: '' });
    setPage(1); // Reset to first page
    setPageCache({}); // Clear cache
    setTotalCount(0); // Reset count to force refetch
    setServerTotals({ totalRolls: 0, totalKicks: 0, totalMovements: 0, count: 0 }); // Reset totals
    showToast('Filters reset', 'info');
    
    // This will trigger fetchMovements via useEffect
  };

  /**
   * Rebuild day-wise statistics from all movement data
   */
  const handleRebuildStats = async () => {
    if (!window.confirm('This will rebuild all statistics from movement data. Continue?')) {
      return;
    }

    try {
      setRebuildingStats(true);
      showToast('Rebuilding statistics...', 'info');
      
      const result = await rebuildAggregateStats(currentUser.email);
      
      // Update local state with rebuilt stats
      setServerTotals({
        totalRolls: result.totalRolls,
        totalKicks: result.totalKicks,
        totalMovements: result.totalRolls + result.totalKicks,
        count: result.count
      });
      
      // Trigger chart refresh
      setRefreshTrigger(prev => prev + 1);
      
      showToast('Statistics rebuilt successfully! 🎉', 'success');
    } catch (error) {
      console.error('Error rebuilding stats:', error);
      showToast('Failed to rebuild statistics', 'error');
    } finally {
      setRebuildingStats(false);
    }
  };

  /**
   * Export data to Excel
   */
  const handleExportToExcel = () => {
    try {
      setExportLoading(true);
      
      const dataToExport = viewMode === 'individual' 
        ? prepareIndividualData() 
        : prepareSessionData();
      
      if (dataToExport.length === 0) {
        showToast('No data to export', 'error');
        return;
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Baby Movements');
      
      // Generate filename with current date
      const filename = `baby_movements_${getISTDateString()}.xlsx`;
      
      // Download file
      XLSX.writeFile(workbook, filename);
      
      showToast('Excel file downloaded successfully! 📊', 'success');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showToast('Failed to export data', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  /**
   * Prepare individual movement data for export
   */
  const prepareIndividualData = () => {
    return filteredMovements.map(m => ({
      'Date': m.dateIST,
      'Time': m.timeIST,
      'Rolls': m.rolls,
      'Kicks': m.kicks,
      'Type': m.type.charAt(0).toUpperCase() + m.type.slice(1)
    }));
  };

  /**
   * Prepare session grouped data for export
   */
  const prepareSessionData = () => {
    const sessions = groupMovementsBySession(filteredMovements, sessionMinutes / 60);
    return sessions.map(s => ({
      'Session Start': formatDisplayDateTime(s.startTime),
      'Session End': formatDisplayDateTime(s.endTime),
      'Total Rolls': s.rolls,
      'Total Kicks': s.kicks,
      'Total Movements': s.rolls + s.kicks,
      'Number of Entries': s.movements.length
    }));
  };

  /**
   * Get display data based on view mode
   */
  const getDisplayData = () => {
    if (viewMode === 'individual') {
      return filteredMovements;
    } else {
      // If sessionMinutes is 0, empty, or invalid, show all data without grouping
      if (!sessionMinutes || sessionMinutes === 0) {
        return filteredMovements;
      }
      return groupMovementsBySession(filteredMovements, sessionMinutes / 60);
    }
  };

  const displayData = getDisplayData();

  // Pagination logic
  const totalItems = viewMode === 'individual' || !sessionMinutes || sessionMinutes === 0 
    ? totalCount 
    : getDisplayData().length; // For session view, we need all data
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedData = viewMode === 'individual' || !sessionMinutes || sessionMinutes === 0
    ? filteredMovements // Already paginated from server
    : displayData.slice((page - 1) * pageSize, page * pageSize); // Client-side pagination for sessions

  const handlePageChange = (newPage) => {
    if (newPage !== page) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setPage(1);
    setPageCache({}); // Clear cache when page size changes
    setTotalCount(0); // Force recount
  };

  // Render loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading movements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                👶 Baby Movement Tracker
              </h1>
              <p className="text-gray-600">
                Logged in as: <span className="font-semibold text-green-600">{currentUser.email}</span>
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="bg-green-100 px-6 py-3 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">Total Tracked</p>
                <p className="text-3xl font-bold text-green-600 mb-2">{serverTotals.totalMovements}</p>
                <div className="flex gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Rolls: </span>
                    <span className="font-semibold text-green-700">{serverTotals.totalRolls}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Kicks: </span>
                    <span className="font-semibold text-blue-600">{serverTotals.totalKicks}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <button
              onClick={handleAddRoll}
              disabled={addingRoll}
              className="tracker-button-primary"
            >
              {addingRoll ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Adding...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <span className="text-xl mr-2">🔄</span>
                  Add Roll
                </span>
              )}
            </button>

            <button
              onClick={handleAddKick}
              disabled={addingKick}
              className="tracker-button-secondary"
            >
              {addingKick ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Adding...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <span className="text-xl mr-2">👣</span>
                  Add Kick
                </span>
              )}
            </button>

            <button
              onClick={handleExportToExcel}
              disabled={exportLoading || movements.length === 0}
              className="bg-brown-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-brown-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportLoading ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Exporting...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <span className="text-xl mr-2">📊</span>
                  Export Excel
                </span>
              )}
            </button>

            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="border-2 border-gray-300 rounded-lg px-4 py-3 font-semibold focus:border-green-600 focus:outline-none"
            >
              <option value="individual">📋 Individual View</option>
              <option value="session">📊 Session View ({sessionMinutes}min)</option>
            </select>

            <button
              onClick={() => setShowChart(!showChart)}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors duration-200"
            >
              <span className="flex items-center justify-center">
                <span className="text-xl mr-2">{showChart ? '📋' : '📈'}</span>
                {showChart ? 'View Table' : 'View Chart'}
              </span>
            </button>
          </div>
        </div>

        {/* Day-wise Statistics Chart */}
        {showChart && (
          <div className="mb-6">
            <DayWiseStatsChart userId={currentUser.email} refreshTrigger={refreshTrigger} />
          </div>
        )}

        {/* Rebuild Stats Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">🔧 Maintenance</h3>
              <p className="text-sm text-gray-600">Rebuild statistics if you notice any inconsistencies</p>
            </div>
            <button
              onClick={handleRebuildStats}
              disabled={rebuildingStats}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {rebuildingStats ? (
                <span className="flex items-center justify-center">
                  <span className="animate-spin mr-2">⏳</span>
                  Rebuilding...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <span className="text-xl mr-2">🔄</span>
                  Rebuild All Stats
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto flex items-center justify-between md:justify-start text-lg font-semibold text-gray-900 mb-4"
          >
            <span>🔍 Filters</span>
            <span className="ml-2">{showFilters ? '▲' : '▼'}</span>
          </button>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:border-green-600 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:border-green-600 focus:outline-none"
                />
              </div>

              {viewMode === 'session' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Minutes
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1440"
                    step="5"
                    value={sessionMinutes}
                    onChange={(e) => setSessionMinutes(e.target.value === '' ? '' : parseInt(e.target.value))}
                    className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:border-green-600 focus:outline-none"
                  />
                </div>
              )}

              <div className="flex items-end space-x-2">
                <button
                  onClick={applyFilters}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors"
                >
                  Apply
                </button>
                <button
                  onClick={resetFilters}
                  className="flex-1 border-2 border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {filteredMovements.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">👶</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No movements recorded yet
              </h3>
              <p className="text-gray-500 mb-6">
                Start tracking by clicking "Add Roll" or "Add Kick" above
              </p>
            </div>
          ) : viewMode === 'individual' || !sessionMinutes || sessionMinutes === 0 ? (
            <>
              <div className="flex flex-wrap justify-between items-center px-4 py-2">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Rows per page:</label>
                  <select value={pageSize} onChange={handlePageSizeChange} className="border rounded px-2 py-1">
                    {[5, 10, 20, 50, 100].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                  >Prev</button>
                  <span className="text-sm">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                  >Next</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-green-600 text-white">
                    <tr>
                      <th className="tracker-table-header">Date & Time</th>
                      <th className="tracker-table-header text-center">Rolls</th>
                      <th className="tracker-table-header text-center">Kicks</th>
                      <th className="tracker-table-header text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedData.map((movement) => (
                      <tr key={movement.id} className="tracker-table-row">
                        <td className="tracker-table-cell">
                          <div className="font-medium text-gray-900">
                            {formatDisplayDateTime(movement.date)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {movement.type === 'roll' ? '🔄 Roll' : '👣 Kick'}
                          </div>
                        </td>
                        <td className="tracker-table-cell text-center">
                          <span className="text-lg font-semibold text-green-600">
                            {movement.rolls}
                          </span>
                        </td>
                        <td className="tracker-table-cell text-center">
                          <span className="text-lg font-semibold text-blue-600">
                            {movement.kicks}
                          </span>
                        </td>
                        <td className="tracker-table-cell text-center">
                          <button
                            onClick={() => handleDelete(movement.id)}
                            className="text-red-600 hover:text-red-800 font-semibold"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-wrap justify-between items-center px-4 py-2">
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600">Rows per page:</label>
                  <select value={pageSize} onChange={handlePageSizeChange} className="border rounded px-2 py-1">
                    {[5, 10, 20, 50, 100].map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                  >Prev</button>
                  <span className="text-sm">Page {page} of {totalPages}</span>
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 rounded border bg-gray-100 text-gray-600 disabled:opacity-50"
                  >Next</button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-green-600 text-white">
                    <tr>
                      <th className="tracker-table-header">Session Period</th>
                      <th className="tracker-table-header text-center">Total Rolls</th>
                      <th className="tracker-table-header text-center">Total Kicks</th>
                      <th className="tracker-table-header text-center">Entries</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedData.map((session, index) => (
                      <tr key={index} className="tracker-table-row">
                        <td className="tracker-table-cell">
                          <div className="font-medium text-gray-900">
                            {formatDateRange(session.startTime, session.endTime)}
                          </div>
                        </td>
                        <td className="tracker-table-cell text-center">
                          <span className="text-lg font-semibold text-green-600">
                            {session.rolls}
                          </span>
                        </td>
                        <td className="tracker-table-cell text-center">
                          <span className="text-lg font-semibold text-blue-600">
                            {session.kicks}
                          </span>
                        </td>
                        <td className="tracker-table-cell text-center">
                          <span className="text-gray-600">
                            {session.movements.length}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Totals Footer */}
          {filteredMovements.length > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex justify-end items-center">
                <div className="text-sm text-gray-500">
                  Showing {paginatedData.length} of {viewMode === 'individual' || !sessionMinutes || sessionMinutes === 0 ? totalCount : filteredMovements.length} entries (page {page} of {totalPages})
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default BabyMovementTracker;
